/* Routine upsell — adds the matching routine (bundle) to the cart from a
   product page, reusing the theme's cart drawer / cart toast rendering.
   Also handles lazy playback for the optional routine video. */

if (!customElements.get('routine-upsell-form')) {
  customElements.define(
    'routine-upsell-form',
    class RoutineUpsellForm extends HTMLElement {
      constructor() {
        super();
        this.onClick = this.onClick.bind(this);
      }

      connectedCallback() {
        this.button = this.querySelector('.routine-upsell__button');
        this.errorEl = this.querySelector('.routine-upsell__form-error');
        if (this.button) {
          this.button.addEventListener('click', this.onClick);
        }
      }

      disconnectedCallback() {
        if (this.button) {
          this.button.removeEventListener('click', this.onClick);
        }
      }

      get sectionsToRender() {
        return [
          { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
          { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
          { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
        ];
      }

      setError(message) {
        if (!this.errorEl) return;
        if (message) {
          this.errorEl.textContent = message;
          this.errorEl.removeAttribute('hidden');
        } else {
          this.errorEl.textContent = '';
          this.errorEl.setAttribute('hidden', '');
        }
      }

      setLoading(state) {
        if (!this.button) return;
        this.button.classList.toggle('is-loading', state);
        this.button.setAttribute('aria-disabled', state ? 'true' : 'false');
        this.button.disabled = state;
      }

      onClick(event) {
        event.preventDefault();

        const variantId = this.button && this.button.dataset.variantId;
        if (!variantId || this.button.classList.contains('is-loading')) return;

        this.setError(false);
        this.setLoading(true);

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', 1);
        formData.append('sections', this.sectionsToRender.map((section) => section.section).join(','));
        formData.append('sections_url', window.location.pathname);

        const addUrl =
          (window.theme && window.theme.routes && window.theme.routes.cart_add_url) || '/cart/add';

        fetch(addUrl, {
          method: 'POST',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            Accept: 'application/javascript'
          },
          body: formData
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.setError(response.description || response.message);
              return;
            }
            this.renderContents(response, variantId);
            if (typeof dispatchCustomEvent === 'function') {
              dispatchCustomEvent('cart:item-added', {
                product: Object.prototype.hasOwnProperty.call(response, 'items')
                  ? response.items[0]
                  : response
              });
            }
          })
          .catch(() => {
            this.setError('אירעה שגיאה, נסי שוב.');
          })
          .finally(() => {
            this.setLoading(false);
          });
      }

      getSectionInnerHTML(html, selector) {
        if (!html) return '';
        const el = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
        return el ? el.innerHTML : '';
      }

      renderContents(parsedState, variantId) {
        if (!parsedState || !parsedState.sections) return;

        this.sectionsToRender.forEach((section) => {
          const container = document.getElementById(section.id);
          if (!container) return;

          const elementToReplace = container.querySelector(section.selector) || container;
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );

          if (section.id === 'Cart-Drawer') {
            const drawer = document.getElementById('Cart-Drawer');
            if (drawer && typeof drawer.notesToggle === 'function') drawer.notesToggle();
            if (drawer && typeof drawer.removeProductEvent === 'function') drawer.removeProductEvent();
          }
          if (section.id === 'Cart' && typeof Cart !== 'undefined') {
            new Cart().renderContents(parsedState);
          }
        });

        const useToast =
          typeof window.shouldUseCartAddToast === 'function' ? window.shouldUseCartAddToast() : false;
        const addedItem =
          typeof window.resolveCartAddedLineItem === 'function'
            ? window.resolveCartAddedLineItem(parsedState, variantId)
            : null;

        if (useToast && addedItem && typeof window.showCartAddToast === 'function') {
          window.showCartAddToast(addedItem);
          return;
        }

        const cartDrawerEl = document.getElementById('Cart-Drawer');
        if (cartDrawerEl) {
          document.body.classList.add('open-cc');
          document.body.classList.add('open-cart');
          cartDrawerEl.classList.add('active');
          const rec = cartDrawerEl.querySelector('.product-recommendations--full');
          if (rec) rec.classList.add('active');
          if (typeof dispatchCustomEvent === 'function') {
            dispatchCustomEvent('cart-drawer:open');
          }
        }
      }
    }
  );
}

/* The routine video is rendered with preload="none" and is only downloaded and
   played once it scrolls into view, so it costs nothing on page load. It also
   pauses when it leaves the viewport and respects reduced-motion. */
if (!customElements.get('routine-upsell-video')) {
  customElements.define(
    'routine-upsell-video',
    class RoutineUpsellVideo extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        if (!this.video) return;

        this.video.setAttribute('playsinline', '');
        this.video.muted = true;

        const wantsAutoplay = this.dataset.autoplay !== 'false';
        const reducedMotion =
          window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        if (!wantsAutoplay || reducedMotion || !('IntersectionObserver' in window)) {
          this.video.setAttribute('preload', 'metadata');
          this.video.setAttribute('controls', '');
          return;
        }

        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                if (this.video.getAttribute('preload') === 'none') {
                  this.video.setAttribute('preload', 'auto');
                  this.video.load();
                }
                const played = this.video.play();
                if (played && typeof played.catch === 'function') played.catch(() => {});
              } else {
                this.video.pause();
              }
            });
          },
          { rootMargin: '200px 0px', threshold: 0.25 }
        );

        this.observer.observe(this);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }
    }
  );
}

/* GA4 / GTM tracking. Fires routine_upsell_render once per page for the
   placement that is actually shown, routine_upsell_visible the first time it
   scrolls into view, and routine_upsell_click on any interaction. Register
   "variant" as an event scoped custom dimension in GA4 to report on it. */
(function () {
  if (window.__routineUpsellTracking) return;
  window.__routineUpsellTracking = true;

  function track(name, params) {
    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        var payload = { event: name };
        for (var k in params) {
          if (Object.prototype.hasOwnProperty.call(params, k)) payload[k] = params[k];
        }
        window.dataLayer.push(payload);
      }
      if (typeof window.gtag === 'function') {
        window.gtag('event', name, params);
      }
      // Shopify Customer Events. GA4 on this store is fed by a sandboxed pixel
      // that theme code cannot reach directly, so events are published here and
      // a custom pixel forwards them to GA4.
      if (
        window.Shopify &&
        window.Shopify.analytics &&
        typeof window.Shopify.analytics.publish === 'function'
      ) {
        window.Shopify.analytics.publish(name, params);
      }
    } catch (e) {}
  }

  function base(el) {
    return {
      variant: el.getAttribute('data-ru-variant') || '',
      routine: el.getAttribute('data-routine-handle') || '',
      product: el.getAttribute('data-product-handle') || ''
    };
  }

  function actionFor(target) {
    if (!target || !target.closest) return 'other';
    if (target.closest('.routine-upsell__quickview')) return 'quick_view';
    if (target.closest('.routine-upsell__button')) return 'add_to_cart';
    if (target.closest('.routine-upsell__link')) return 'details_link';
    if (target.closest('.routine-upsell__item')) return 'component_product';
    if (target.closest('.routine-upsell-card__body')) return 'card_link';
    if (target.closest('.routine-upsell__title')) return 'title_link';
    if (target.closest('.routine-upsell__media')) return 'media_link';
    return 'other';
  }

  function init() {
    var els = document.querySelectorAll('[data-ru-variant]');

    Array.prototype.forEach.call(els, function (el) {
      // Skip the placement hidden by the A/B split - it has no layout box.
      if (el.getClientRects().length === 0) return;

      track('routine_upsell_render', base(el));

      el.addEventListener('click', function (e) {
        var p = base(el);
        p.action = actionFor(e.target);
        track('routine_upsell_click', p);
      });

      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(
          function (entries) {
            for (var i = 0; i < entries.length; i++) {
              if (entries[i].isIntersecting) {
                track('routine_upsell_visible', base(el));
                io.disconnect();
                return;
              }
            }
          },
          { threshold: 0.5 }
        );
        io.observe(el);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
